import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box, Input, InputGroup, InputLeftElement, InputRightElement,
    VStack, HStack, Text, Badge, Icon, Spinner, Flex,
} from "@chakra-ui/react";
import { MdSearch, MdClose, MdAssignment } from "react-icons/md";
import axios from "axios";
import { BASE_URL } from "config/api";

const statusColor = {
    "créée"       : { bg: "#EBF8FF", color: "#2B6CB0", border: "#BEE3F8" },
    "en cours"    : { bg: "#FFFBEB", color: "#92400E", border: "#FDE68A" },
    "terminée"    : { bg: "#F0FFF4", color: "#276749", border: "#C6F6D5" },
    "annulée"     : { bg: "#FFF5F5", color: "#9B2C2C", border: "#FED7D7" },
    "dérangement" : { bg: "#FAF5FF", color: "#6B356C", border: "#E9D8FD" },
};

const typeShort = {
    "ambulance"              : "Amb",
    "Téléconsultation"       : "Télé",
    "Consultation à domicile": "Cons",
    "acte infirmier"         : "Infm",
};

/**
 * MissionSearchInput
 *
 * Props:
 *  - value          : mission_id actuellement sélectionné (number | null)
 *  - onChange       : (mission) => void  — appelé avec l'objet mission sélectionné (ou null si effacé)
 *  - placeholder    : string
 *  - isDisabled     : bool
 *  - defaultMission : objet mission pré-sélectionné (optionnel, évite un fetch initial)
 */
const MissionSearchInput = ({
    value,
    onChange,
    placeholder = "Rechercher par n° mission, patient, ville...",
    isDisabled  = false,
    defaultMission = null,
}) => {
    const [missions, setMissions]         = useState([]);
    const [loading, setLoading]           = useState(false);
    const [search, setSearch]             = useState("");
    const [open, setOpen]                 = useState(false);
    const [selected, setSelected]         = useState(null);
    const containerRef                    = useRef(null);
    const inputRef                        = useRef(null);

    // Charger les missions une seule fois
    const fetchMissions = useCallback(async () => {
        if (missions.length > 0) return;
        setLoading(true);
        try {
            const res = await axios.get(`${BASE_URL}/missions`, { withCredentials: true });
            const data = Array.isArray(res.data) ? res.data : [];
            // Trier du plus récent au plus ancien
            data.sort((a, b) => b.id - a.id);
            setMissions(data);
        } catch (_) {
            // silencieux
        } finally {
            setLoading(false);
        }
    }, [missions.length]);

    // Pré-sélectionner via prop value ou defaultMission
    useEffect(() => {
        if (defaultMission) {
            setSelected(defaultMission);
            return;
        }
        if (value && missions.length > 0) {
            const found = missions.find((m) => m.id === value);
            if (found) setSelected(found);
        }
    }, [value, missions, defaultMission]);

    // Pré-sélectionner depuis defaultMission au montage immédiat
    useEffect(() => {
        if (defaultMission) setSelected(defaultMission);
    }, [defaultMission]);

    // Fermer le dropdown si clic hors du composant
    useEffect(() => {
        const handleOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, []);

    const filtered = missions.filter((m) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        const patientName = [m.patient?.nom, m.patient?.prenom].filter(Boolean).join(" ").toLowerCase();
        return (
            String(m.id).includes(q) ||
            (m.date_mission || "").toLowerCase().includes(q) ||
            (m.type_mission || "").toLowerCase().includes(q) ||
            (m.statut || "").toLowerCase().includes(q) ||
            (m.ville || "").toLowerCase().includes(q) ||
            patientName.includes(q)
        );
    }).slice(0, 30);

    const handleOpen = () => {
        if (isDisabled) return;
        fetchMissions();
        setOpen(true);
        setSearch("");
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    const handleSelect = (mission) => {
        setSelected(mission);
        setOpen(false);
        setSearch("");
        onChange && onChange(mission);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setSelected(null);
        setSearch("");
        onChange && onChange(null);
    };

    const statusConf = selected ? (statusColor[selected.statut] || statusColor["créée"]) : null;

    return (
        <Box ref={containerRef} position="relative">
            {/* ── Champ affiché ──────────────────────────────── */}
            {selected ? (
                /* Mission sélectionnée */
                <Flex
                    align="center"
                    border="1px solid"
                    borderColor="blue.300"
                    borderRadius="8px"
                    px={3}
                    py={2}
                    bg="blue.50"
                    cursor={isDisabled ? "default" : "pointer"}
                    onClick={isDisabled ? undefined : handleOpen}
                    _hover={isDisabled ? {} : { borderColor: "blue.400" }}
                    gap={2}
                >
                    <Icon as={MdAssignment} color="blue.500" flexShrink={0} />
                    <VStack align="start" spacing={0} flex={1} minW={0}>
                        <Text fontSize="sm" fontWeight="semibold" color="blue.900" noOfLines={1}>
                            Mission #{selected.id}
                            {selected.date_mission && (
                                <Text as="span" fontWeight="normal" color="blue.600" ml={2} fontSize="xs">
                                    — {selected.date_mission}
                                </Text>
                            )}
                        </Text>
                        <HStack spacing={2} flexWrap="wrap">
                            {selected.type_mission && (
                                <Text fontSize="xs" color="blue.500">{selected.type_mission}</Text>
                            )}
                            {selected.ville && (
                                <Text fontSize="xs" color="gray.500">{selected.ville}</Text>
                            )}
                            {selected.statut && (
                                <Badge
                                    fontSize="10px"
                                    px={1.5}
                                    py={0.5}
                                    borderRadius="full"
                                    bg={statusConf?.bg}
                                    color={statusConf?.color}
                                    border="1px solid"
                                    borderColor={statusConf?.border}
                                    textTransform="none"
                                >
                                    {selected.statut}
                                </Badge>
                            )}
                        </HStack>
                    </VStack>
                    {!isDisabled && (
                        <Icon
                            as={MdClose}
                            color="blue.400"
                            _hover={{ color: "red.500" }}
                            onClick={handleClear}
                            flexShrink={0}
                        />
                    )}
                </Flex>
            ) : (
                /* Aucune sélection — champ de recherche */
                <InputGroup>
                    <InputLeftElement pointerEvents="none">
                        <Icon as={MdSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            if (!open) { fetchMissions(); setOpen(true); }
                        }}
                        onFocus={handleOpen}
                        placeholder={placeholder}
                        isDisabled={isDisabled}
                        autoComplete="off"
                        ref={inputRef}
                    />
                    {loading && (
                        <InputRightElement>
                            <Spinner size="xs" color="gray.400" />
                        </InputRightElement>
                    )}
                </InputGroup>
            )}

            {/* ── Dropdown ───────────────────────────────────── */}
            {open && !isDisabled && (
                <Box
                    position="absolute"
                    top="calc(100% + 4px)"
                    left={0}
                    right={0}
                    zIndex={1500}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="10px"
                    boxShadow="lg"
                    maxH="280px"
                    overflowY="auto"
                >
                    {/* Barre de recherche dans le dropdown (si une mission était sélectionnée) */}
                    {selected && (
                        <Box p={2} borderBottom="1px solid" borderColor="gray.100">
                            <InputGroup size="sm">
                                <InputLeftElement pointerEvents="none">
                                    <Icon as={MdSearch} color="gray.400" />
                                </InputLeftElement>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Filtrer par n° mission, patient, ville..."
                                    autoFocus
                                    autoComplete="off"
                                />
                            </InputGroup>
                        </Box>
                    )}

                    {loading ? (
                        <Flex justify="center" py={6}>
                            <Spinner size="sm" color="blue.500" />
                            <Text fontSize="sm" color="gray.500" ml={2}>Chargement...</Text>
                        </Flex>
                    ) : filtered.length === 0 ? (
                        <Text fontSize="sm" color="gray.500" px={4} py={4} textAlign="center">
                            Aucune mission trouvée
                        </Text>
                    ) : (
                        <VStack spacing={0} align="stretch">
                            {filtered.map((m) => {
                                const conf    = statusColor[m.statut] || statusColor["créée"];
                                const short   = typeShort[m.type_mission] || m.type_mission || "—";
                                const patient = m.patient
                                    ? `${m.patient.nom || ""} ${m.patient.prenom || ""}`.trim()
                                    : null;
                                return (
                                    <HStack
                                        key={m.id}
                                        px={4}
                                        py={3}
                                        spacing={3}
                                        cursor="pointer"
                                        _hover={{ bg: "blue.50" }}
                                        onClick={() => handleSelect(m)}
                                        borderBottom="1px solid"
                                        borderColor="gray.50"
                                    >
                                        {/* Icône / Type */}
                                        <Flex
                                            w="32px"
                                            h="32px"
                                            bg="blue.100"
                                            borderRadius="6px"
                                            align="center"
                                            justify="center"
                                            flexShrink={0}
                                        >
                                            <Text fontSize="9px" fontWeight="bold" color="blue.700">
                                                {short}
                                            </Text>
                                        </Flex>

                                        <VStack align="start" spacing={0} flex={1} minW={0}>
                                            <HStack spacing={2}>
                                                <Text fontSize="sm" fontWeight="semibold" color="gray.900">
                                                    Mission #{m.id}
                                                </Text>
                                                {m.date_mission && (
                                                    <Text fontSize="xs" color="gray.500">{m.date_mission}</Text>
                                                )}
                                            </HStack>
                                            <HStack spacing={2} flexWrap="wrap">
                                                {patient && (
                                                    <Text fontSize="xs" color="gray.600" noOfLines={1}>{patient}</Text>
                                                )}
                                                {m.ville && (
                                                    <Text fontSize="xs" color="gray.400">{m.ville}</Text>
                                                )}
                                            </HStack>
                                        </VStack>

                                        {m.statut && (
                                            <Badge
                                                fontSize="10px"
                                                px={2}
                                                py={0.5}
                                                borderRadius="full"
                                                bg={conf.bg}
                                                color={conf.color}
                                                border="1px solid"
                                                borderColor={conf.border}
                                                textTransform="none"
                                                flexShrink={0}
                                            >
                                                {m.statut}
                                            </Badge>
                                        )}
                                    </HStack>
                                );
                            })}
                        </VStack>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default MissionSearchInput;
